import { createGameStore, type PersistedGameStore } from '@game-libs/generic-game-state';
import { registerStoreReset } from './index';
import { CONFIG } from '../../config';

export type DragonHealthState = {
  dragonHealth: number;
  remainingPoison: number;
  damageLastTick: number;
};

export type DragonHealthActions = {
  setHealth: (health: number) => void;
  applyDamage: (damage: number) => void;
  setRemainingPoison: (remaining: number) => void;
  setDamageLastTick: (damage: number) => void;
  /** Bulk-sync from the reducer so Zustand persist matches GUI state. */
  sync: (patch: Partial<DragonHealthState>) => void;
  reset: () => void;
};

export type DragonHealthStore = DragonHealthState & DragonHealthActions;

// Single source of truth for dragon-health defaults during migration from context to Zustand.
export const INITIAL_DRAGON_HEALTH_STATE: DragonHealthState = {
  dragonHealth: CONFIG.game.initialDragonHealth.smallDragon,
  remainingPoison: 0,
  damageLastTick: 0,
};

export const useDragonHealthStore: PersistedGameStore<DragonHealthStore> = createGameStore<DragonHealthStore>(
  { saveKey: 'dragon-health' },
  (set) => ({
    ...INITIAL_DRAGON_HEALTH_STATE,

    setHealth: (health) => set({ dragonHealth: health }),

    applyDamage: (damage) =>
      set((s) => ({ dragonHealth: Math.max(0, s.dragonHealth - damage) })),

    setRemainingPoison: (remaining) => set({ remainingPoison: remaining }),

    setDamageLastTick: (damage) => set({ damageLastTick: damage }),

    sync: (patch) => set(patch),

    reset: () => set(INITIAL_DRAGON_HEALTH_STATE),
  }),
);

registerStoreReset(() => useDragonHealthStore.getState().reset());
